import React, { useEffect, useMemo } from 'react';
import {
  IPanelStyles,
  // eslint-disable-next-line no-restricted-imports
  Panel as FluentUIPanel,
  PanelType,
  Stack,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { pageDataService } from 'common/utilities';
import { useLocalizedStrings } from 'i18n';

import { ActionButton } from '../Buttons/v8';
import { PillButton } from '../Buttons/v8/PillButton';
import { BackIcon } from '../Icons/BackIcon';
import { Heading2 } from '../Text';

import { usePanelOverlayStyles, usePanelStyles } from './Panel.styles';
import { IPanelProps } from './Panel.types';
import { usePanelMobileView } from './usePanelMobileView';

export const Panel = (props: IPanelProps) => {
  const {
    styles,
    onRenderHeader,
    onBackLinkClick,
    onDismiss,
    backAriaLabel,
    type,
    bgColorOverride,
    headerText,
    overlayProps,
    ...otherProps
  } = props;
  const { isMobileView } = usePanelMobileView();
  const { classNames } = usePanelStyles();
  const { t } = useLocalizedStrings();
  const isWebView = pageDataService.isWebView();
  const { styles: updatedStyles } = usePanelStyles(styles as Partial<IPanelStyles>, bgColorOverride);
  const panelOverlayStyles = usePanelOverlayStyles(isWebView, overlayProps?.styles);
  const headingId = useId('panel-heading-');

  useEffect(() => {
    // Note: We need to focus on the heading element (when changed) for screen readers to correctly focus on and read the title out loud.
    // Without this, the screen reader will focus on and read the app/page name instead.
    document.getElementById(headingId)?.focus();
  }, [headingId, headerText]);

  // explicitly specify panelType to ensure that breakpoints of the panel match the breakpoints used throughout the app
  const panelType = useMemo(
    () => type ?? (isMobileView || isWebView ? PanelType.smallFluid : PanelType.smallFixedFar),
    [type, isMobileView, isWebView]
  );

  const renderBackIcon = React.useCallback(() => <BackIcon />, []);
  const onBackClick = React.useCallback(() => {
    if (onBackLinkClick) {
      onBackLinkClick();
    } else {
      onDismiss?.();
    }
  }, [onBackLinkClick, onDismiss]);

  const onRenderPanelHeader = React.useCallback(
    (panelProps?: IPanelProps) => {
      const { enableBackNavigation } = panelProps || {};
      const showBackButton = enableBackNavigation || isMobileView;

      // back button aria-label falls back to default "Back to home" if not provided
      const backButtonAriaLabel = backAriaLabel ?? t('BackAriaLabel');

      if (headerText || showBackButton) {
        return (
          <Stack
            className={clsx(classNames.headerStack, isMobileView && classNames.mobileHeaderGrid)}
            horizontal={isMobileView}
            horizontalAlign={isMobileView ? 'center' : undefined}
          >
            {showBackButton && (
              <Stack.Item grow>
                {isMobileView ? (
                  <PillButton
                    className={classNames.backButton}
                    onRenderIcon={renderBackIcon}
                    onClick={onBackClick}
                    ariaLabel={backButtonAriaLabel}
                    telemetryEventName="panelBackClick"
                  />
                ) : (
                  <ActionButton
                    className={classNames.backButton}
                    onRenderIcon={renderBackIcon}
                    onClick={onBackClick}
                    telemetryEventName="panelBackClick"
                    ariaLabel={backButtonAriaLabel}
                    text={t('Back')}
                  />
                )}
              </Stack.Item>
            )}
            {headerText && (
              <Stack.Item className={classNames.headerText}>
                {/* Note: setting tabIndex is required for screen readers to correctly focus on and read the title out loud */}
                <Heading2 variant={isMobileView ? 'Body1Strong' : 'Title1Strong'} id={headingId} tabIndex={-1}>
                  {headerText}
                </Heading2>
              </Stack.Item>
            )}
          </Stack>
        );
      }
      return null;
    },
    [
      headingId,
      headerText,
      backAriaLabel,
      classNames.backButton,
      classNames.headerStack,
      classNames.headerText,
      classNames.mobileHeaderGrid,
      isMobileView,
      onBackClick,
      renderBackIcon,
      t,
    ]
  );

  return (
    <FluentUIPanel
      // client will render close button outside of WebView
      hasCloseButton={!isMobileView && !isWebView}
      onDismiss={onDismiss}
      onRenderHeader={onRenderHeader ?? onRenderPanelHeader}
      type={panelType}
      styles={updatedStyles}
      overlayProps={{ ...overlayProps, styles: panelOverlayStyles }}
      {...otherProps}
    />
  );
};
